import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { Token } from "@devour/client";
import useGetTopGuildsLeaderboard from "@/hooks/useGetTopGuildsLeaderboard";
import useGetTopGuildLeadersLeaderboard from "@/hooks/useGetTopGuildLeadersLeaderboard";
import useGetGlobalMonthlyGuildLeaderboard from "@/hooks/useGetGlobalMonthlyGuildLeaderboard";
import useGetCurrentGuildTopMembers from "@/hooks/useGetCurrentGuildTopMembers";
import useGetLeaderboardDateRange from "@/hooks/useGetLeaderboardDateRange";
import { ViewMode } from "@/components/goFrens/GoFrensTopLeaderboard";
import { DateTime } from "luxon";
import { FiUsers, FiTrendingUp, FiGlobe, FiUser, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { BiMedal, BiStats } from "react-icons/bi";
import classNames from "classnames";
import "./UnifiedLeaderboardV2.scss";

export enum LeaderboardTab {
    MY_GUILD = "my-guild",
    GUILDS = "guilds",
    GUILD_LEADERS = "guild-leaders",
    GLOBAL = "global",
}

interface UnifiedLeaderboardV2Props {
    defaultTab?: LeaderboardTab;
    className?: string;
    currentGuildId?: string;
}

const ITEMS_PER_PAGE = 10;

export default function UnifiedLeaderboardV2({
    defaultTab = LeaderboardTab.GUILDS,
    className,
    currentGuildId,
}: UnifiedLeaderboardV2Props) {
    const [activeTab, setActiveTab] = useState<LeaderboardTab>(defaultTab);
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.MONTHLY);
    const [currentPage, setCurrentPage] = useState(1);

    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUser = useSelector((store: IStore) => store.authStore.user);

    // Get current month for default selection
    const now = DateTime.now().setZone("America/New_York");
    const [dateValue, setDateValue] = useState<string>(`${now.month},${now.year}`);

    // Build leaderboard parameter
    const leaderboardTimeParam = useMemo(() => {
        return `${dateValue},${viewMode}`;
    }, [dateValue, viewMode]);

    // Date range for month selector
    const { data: dateRange } = useGetLeaderboardDateRange("guild");

    // Fetch data based on active tab
    const { data: guildsData, isLoading: guildsLoading } = useGetTopGuildsLeaderboard(
        leaderboardTimeParam,
        activeTab === LeaderboardTab.GUILDS,
    );

    const { data: guildLeadersData, isLoading: guildLeadersLoading } = useGetTopGuildLeadersLeaderboard(
        leaderboardTimeParam,
        activeTab === LeaderboardTab.GUILD_LEADERS,
    );

    // For global, use the pattern from GuildsLeaderboardCard - pass dateParam,0 for monthly
    const { data: globalData, isLoading: globalLoading } = useGetGlobalMonthlyGuildLeaderboard(
        `${dateValue},0`, // Always pass 0 for this hook
        activeTab === LeaderboardTab.GLOBAL,
    );

    const { data: myGuildData, isLoading: myGuildLoading } = useGetCurrentGuildTopMembers(
        fullToken,
        leaderboardTimeParam,
        activeTab === LeaderboardTab.MY_GUILD && !!currentGuildId,
    );

    // Tab configuration
    const tabs = [
        {
            id: LeaderboardTab.MY_GUILD,
            label: "My Guild",
            icon: <FiUser />,
            disabled: !currentGuildId,
        },
        {
            id: LeaderboardTab.GUILDS,
            label: "Guilds",
            icon: <FiUsers />,
        },
        {
            id: LeaderboardTab.GUILD_LEADERS,
            label: "Guild Leaders",
            icon: <BiMedal />,
        },
        {
            id: LeaderboardTab.GLOBAL,
            label: "Global",
            icon: <FiGlobe />,
        },
    ];

    // Get active data and loading state
    const getActiveData = () => {
    // Helper function to filter out entries with 0 XP
        const filterZeroXP = (data: any[]) => {
            if (!data) return [];
            return data.filter(entry => {
                const points = entry.totalPoints || entry.monthlyPoints || entry.accumulatedPoints || entry.points || entry.xp || 0;
                return points > 0;
            });
        };

        switch (activeTab) {
            case LeaderboardTab.MY_GUILD:
                return {
                    data: filterZeroXP(myGuildData?.members || myGuildData || []),
                    isLoading: myGuildLoading,
                };
            case LeaderboardTab.GUILDS:
                return {
                    data: filterZeroXP(guildsData?.guilds || []),
                    isLoading: guildsLoading,
                };
            case LeaderboardTab.GUILD_LEADERS:
                return {
                    data: filterZeroXP(guildLeadersData?.leaders || []),
                    isLoading: guildLeadersLoading,
                };
            case LeaderboardTab.GLOBAL:
                return {
                    data: filterZeroXP(globalData?.members || globalData?.leaders || []),
                    isLoading: globalLoading,
                };
            default:
                return { data: [], isLoading: false };
        }
    };

    const { data: activeData, isLoading } = getActiveData();

    // Find current user's position
    const userPosition = useMemo(() => {
        if (!currentUser || !activeData) return null;

        const userIndex = activeData.findIndex((entry: any) => {
            // Check various ways the user might be identified
            return entry.userId === currentUser.id ||
             entry.nickname === currentUser.nickname ||
             entry.name === currentUser.nickname;
        });

        if (userIndex === -1) return null;

        return {
            rank: userIndex + 1,
            data: activeData[userIndex],
        };
    }, [currentUser, activeData]);

    // Pagination
    const totalPages = Math.ceil((activeData?.length || 0) / ITEMS_PER_PAGE);
    const paginatedData = activeData?.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
    );

    // Generate month options
    const monthOptions = useMemo(() => {
        const options = [];

        options.push({
            value: `${now.month},${now.year}`,
            label: "Current Month",
        });

        if (dateRange?.dates) {
            dateRange.dates.forEach(date => {
                options.push({
                    value: date.dateParam,
                    label: date.dateValue,
                });
            });
        }

        return options;
    }, [dateRange, now.month, now.year]);

    const getRankDisplay = (rank: number) => {
        if (rank === 1) return "🥇";
        if (rank === 2) return "🥈";
        if (rank === 3) return "🥉";
        return rank;
    };

    const getPoints = (entry: any) => {
        return entry.totalPoints || entry.monthlyPoints || entry.accumulatedPoints || entry.points || entry.xp || 0;
    };

    return (
        <div className={classNames("unified-leaderboard-v2", className)}>
            {/* Header */}
            <div className="unified-leaderboard-v2__header">
                <div className="header-left">
                    <BiStats className="icon" />
                    <h3 className="title">Leaderboards</h3>
                </div>

                <div className="controls">
                    <select
                        className="month-selector"
                        value={dateValue}
                        onChange={(e) => {
                            setDateValue(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        {monthOptions.map(option =>
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>)}
                    </select>

                    <div className="view-toggle">
                        <button
                            className={classNames({ active: viewMode === ViewMode.MONTHLY })}
                            onClick={() => setViewMode(ViewMode.MONTHLY)}
                        >
                            Monthly
                        </button>
                        <button
                            className={classNames({ active: viewMode === ViewMode.CUMULATIVE })}
                            onClick={() => setViewMode(ViewMode.CUMULATIVE)}
                        >
                            All-Time
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="unified-leaderboard-v2__tabs">
                {tabs.map(tab =>
                    <button
                        key={tab.id}
                        className={classNames("tab", {
                            active: activeTab === tab.id,
                            disabled: tab.disabled,
                        })}
                        onClick={() => {
                            if (!tab.disabled) {
                                setActiveTab(tab.id);
                                setCurrentPage(1);
                            }
                        }}
                        disabled={tab.disabled}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>)}
            </div>

            {/* Content */}
            <div className="unified-leaderboard-v2__content">
                {isLoading
                    ? <div className="loading-state">
                        <div className="spinner" />
                        <span>Loading...</span>
                    </div>
                    : !activeData || activeData.length === 0
                        ? <div className="empty-state">
                            <span>No players with XP this period</span>
                        </div>
                        : <>
                            {/* Pinned User Position (if not in current page) */}
                            {userPosition && (userPosition.rank < (currentPage - 1) * ITEMS_PER_PAGE + 1 ||
                              userPosition.rank > currentPage * ITEMS_PER_PAGE) &&
              <div className="pinned-position">
                  <div className="position-row highlighted">
                      <div className="rank">{getRankDisplay(userPosition.rank)}</div>
                      <div className="player">
                          <img
                              src={userPosition.data.profilePicture || userPosition.data.photo || userPosition.data.icon || "/images/default-avatar.png"}
                              alt=""
                              className="avatar"
                          />
                          <div className="player-text">
                              <span className="name">
                                  {userPosition.data.nickname || userPosition.data.name || "Player"} (You)
                              </span>
                          </div>
                      </div>
                      <div className="xp">{getPoints(userPosition.data).toLocaleString()}</div>
                  </div>
              </div>
                            }

                            {/* Leaderboard Table */}
                            <div className="leaderboard-table">
                                <div className="table-header">
                                    <div className="rank">Rank</div>
                                    <div className="player">{activeTab === LeaderboardTab.GUILDS ? "Guild" : "Player"}</div>
                                    <div className="xp">XP</div>
                                </div>

                                <div className="table-body">
                                    {paginatedData.map((entry: any, index: number) => {
                                        const globalRank = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                                        // Fix user identification - be more specific
                                        const isUser = currentUser && (
                                            entry.userId && entry.userId === currentUser.id ||
                    entry.id && entry.id === currentUser.id ||
                    entry.nickname && currentUser.nickname && entry.nickname === currentUser.nickname
                                        );

                                        return (
                                            <div
                                                key={entry.id || entry.userId || index}
                                                className={classNames("position-row", {
                                                    highlighted: isUser,
                                                    "top-three": globalRank <= 3,
                                                    even: index % 2 === 0,
                                                })}
                                            >
                                                <div className="rank">{getRankDisplay(globalRank)}</div>
                                                <div className="player">
                                                    <img
                                                        src={entry.profilePicture || entry.photo || entry.icon || "/images/default-avatar.png"}
                                                        alt=""
                                                        className="avatar"
                                                    />
                                                    <div className="player-text">
                                                        <span className="name">
                                                            {entry.nickname || entry.name || "Player"}
                                                            {isUser && " (You)"}
                                                        </span>
                                                        {(activeTab === LeaderboardTab.GUILD_LEADERS || activeTab === LeaderboardTab.GLOBAL) && entry.guildName &&
                            <span className="guild-name">{entry.guildName}</span>
                                                        }
                                                    </div>
                                                </div>
                                                <div className="xp">{getPoints(entry).toLocaleString()}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 &&
              <div className="pagination">
                  <button
                      className="page-btn"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                  >
                      <FiChevronLeft />
                  </button>

                  <div className="page-info">
                      Rank {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, activeData.length)}
                      <span className="total">Total {activeData.length} Participants</span>
                  </div>

                  <button
                      className="page-btn"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                  >
                      <FiChevronRight />
                  </button>
              </div>
                            }
                        </>
                }
            </div>
        </div>
    );
}